/**
 * Received Document Page (Subir documento de compra)
 *
 * Costa Rica's Alanube has no reception inbox to poll, so — like the legacy
 * system — the user uploads the supplier's electronic document XML (received by
 * email). The backend parses Clave + Emisor + ResumenFactura, and the user
 * accepts / partially accepts / rejects it via a Mensaje Receptor (Hacienda
 * v4.4). Accepting creates a Purchase (accounts-payable) record. Inventory is
 * NOT touched here — stock is managed in the product edit screen.
 */

import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Upload, Check, CheckCheck, X, Loader2, FileText, AlertTriangle } from 'lucide-react'

import { formatCurrency, formatDate } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toaster'
import {
  useParseSupplierXml,
  useAcceptReceivedDocument,
  useRejectReceivedDocument,
  type ReceivedDocument,
} from '@/hooks/useInvoicing'
import { useIsMobile } from '@/hooks/useIsMobile'

type DialogMode = 'partial' | 'reject'

export function ReceivedDocumentsPage() {
  const navigate = useNavigate()
  // En el celular el autofoco abre el teclado y tapa la mitad de la pantalla
  const isMobile = useIsMobile()
  const { t } = useTranslation()
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const parse = useParseSupplierXml()
  const accept = useAcceptReceivedDocument()
  const reject = useRejectReceivedDocument()

  const [doc, setDoc] = useState<ReceivedDocument | null>(null)
  const [dialog, setDialog] = useState<DialogMode | null>(null)
  const [detailText, setDetailText] = useState('')
  const [acceptError, setAcceptError] = useState<string | null>(null)

  const busy = accept.isPending || reject.isPending
  // Hacienda solo deja responder al receptor del documento: si el XML viene a
  // otra cédula, el Mensaje Receptor siempre será rechazado.
  const blocked = !!doc?.receiver_mismatch

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setAcceptError(null)
    parse.mutate(file, {
      onSuccess: (d) => setDoc(d),
      onError: (err: any) =>
        toast({
          title: t('purchases.receivedDoc.xmlReadError'),
          description: err?.response?.data?.detail || t('purchases.receivedDoc.xmlReadErrorHint'),
          variant: 'destructive',
        }),
    })
    // allow re-uploading the same file name later
    e.target.value = ''
  }

  const reset = () => {
    setDoc(null)
    setDialog(null)
    setDetailText('')
    setAcceptError(null)
  }

  const onResult = (okTitle: string) => (res: any) => {
    if (res?.success === false) {
      // Keep the error on screen (not just a transient toast): the failed
      // acceptance is logged as an incident with the request/response JSON and
      // possible causes, reachable from Configuración → Incidencias.
      setAcceptError(res?.message || t('purchases.receivedDoc.processFailedHacienda'))
      setDialog(null)
      return
    }
    toast({ title: okTitle, variant: 'success' })
    reset()
    navigate('/purchases')
  }

  const onError = (err: any) =>
    toast({
      title: t('common.error'),
      description: err?.response?.data?.detail || t('purchases.receivedDoc.processFailed'),
      variant: 'destructive',
    })

  const onAcceptTotal = () => {
    if (!doc) return
    accept.mutate({ document: doc, messageType: '1' }, { onSuccess: onResult(t('purchases.receivedDoc.accepted')), onError })
  }

  const submitDialog = () => {
    if (!doc || !dialog) return
    const text = detailText.trim()
    if (!text) {
      toast({ title: t('purchases.receivedDoc.detailMissing'), description: t('purchases.receivedDoc.detailMissingHint'), variant: 'destructive' })
      return
    }
    if (dialog === 'partial') {
      accept.mutate(
        { document: doc, messageType: '2', detailMessage: text },
        { onSuccess: onResult(t('purchases.receivedDoc.partialSent')), onError }
      )
    } else {
      reject.mutate(
        { document: doc, rejectionReason: text },
        { onSuccess: onResult(t('purchases.receivedDoc.rejected')), onError }
      )
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/purchases')} className="shrink-0" aria-label={t('common.back')}>
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold">{t('purchases.receivedDoc.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('purchases.receivedDoc.subtitle')}
          </p>
        </div>
      </div>

      {/* Upload */}
      <Card>
        <CardContent className="p-6">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml,text/xml,application/xml"
            className="hidden"
            onChange={onFile}
          />
          <Button
            variant="outline"
            className="w-full h-24 border-dashed flex-col gap-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={parse.isPending}
          >
            {parse.isPending ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Upload className="h-6 w-6" />
            )}
            <span>{doc ? t('purchases.receivedDoc.uploadAnother') : t('purchases.receivedDoc.selectXml')}</span>
          </Button>
        </CardContent>
      </Card>

      {parse.isPending && <Skeleton className="h-40 w-full" />}

      {/* Parsed document */}
      {doc && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{doc.issuer_name || doc.issuer_id || t('purchases.receivedDoc.provider')}</span>
            </div>

            {doc.receiver_mismatch && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <span>
                    {t('purchases.receiverMismatch', {
                      receiverName: doc.receiver_name || '—',
                      receiverId: doc.receiver_id || '—',
                      tenantId: doc.tenant_identification || '—',
                    })}
                  </span>
                  <p className="text-xs">{t('purchases.receiverMismatchAction')}</p>
                </div>
              </div>
            )}
            {!doc.provider_exists && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{t('purchases.receivedDoc.providerNotFound')}</span>
              </div>
            )}
            {doc.provider_exists && doc.provider_needs_update && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <span>
                    {t('purchases.providerDataMismatch', {
                      name: doc.provider_current_name || doc.issuer_name,
                    })}
                  </span>
                  <p className="text-xs">
                    {t('purchases.providerDataMismatchDetail', {
                      currentName: doc.provider_current_name || '—',
                      currentId: doc.provider_current_identification || '—',
                      currentType: doc.provider_current_type_identification || '—',
                      xmlName: doc.issuer_name || '—',
                      xmlId: doc.issuer_id || '—',
                      xmlType: doc.issuer_id_type || '—',
                    })}
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">{t('purchases.receivedDoc.issuerId')}</p>
                <p className="font-medium">{doc.issuer_id || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('purchases.receivedDoc.consecutive')}</p>
                <p className="font-medium break-all">{doc.consecutive || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('purchases.receivedDoc.date')}</p>
                <p className="font-medium">{formatDate(doc.issue_date)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('reports.taxLabel')}</p>
                <p className="font-medium">{formatCurrency(doc.total_tax)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">{t('purchases.receivedDoc.key')}</p>
                <p className="font-mono text-xs break-all">{doc.document_key}</p>
              </div>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-muted-foreground">{t('reports.total')}</span>
              <span className="text-xl font-bold text-primary">{formatCurrency(doc.total)}</span>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={onAcceptTotal} disabled={busy || blocked} className="flex-1">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                {t('purchases.receivedDoc.accept')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setDetailText('')
                  setDialog('partial')
                }}
                disabled={busy || blocked}
              >
                <CheckCheck className="h-4 w-4 mr-2" />
                {t('purchases.receivedDoc.partial')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setDetailText('')
                  setDialog('reject')
                }}
                disabled={busy || blocked}
              >
                <X className="h-4 w-4 mr-2" />
                {t('purchases.receivedDoc.reject')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Persistent error after a failed acceptance, with a link to the incident */}
      {acceptError && (
        <Card className="border-destructive/50">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-destructive" />
              <div className="space-y-1">
                <p className="font-medium text-destructive">{t('purchases.receivedDoc.processFailedTitle')}</p>
                <p className="text-sm text-muted-foreground break-words">{acceptError}</p>
                <p className="text-xs text-muted-foreground">{t('purchases.receivedDoc.processFailedHint')}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings/invoicing-incidents')}
            >
              {t('purchases.receivedDoc.viewIncidents')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Detail dialog for partial acceptance / rejection */}
      <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog === 'partial' ? t('purchases.receivedDoc.partialTitle') : t('purchases.receivedDoc.rejectTitle')}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            className="min-h-[100px]"
            aria-label={dialog === 'partial' ? t('purchases.receivedDoc.partialDetail') : t('purchases.receivedDoc.rejectReason')}
            placeholder={dialog === 'partial' ? t('purchases.receivedDoc.partialDetail') : t('purchases.receivedDoc.rejectReason')}
            value={detailText}
            onChange={(e) => setDetailText(e.target.value)}
            autoFocus={!isMobile}
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={dialog === 'reject' ? 'destructive' : 'default'}
              onClick={submitDialog}
              disabled={busy}
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
